import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AlertTriangle, Clock, TrendingUp, Layers, BarChart3, Eye, X } from 'lucide-react';
import observabilityService from '../../services/observabilityService';

const StatCard = ({ label, value, icon: Icon, color, loading }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  };
  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-16" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
        </div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900">
            {value != null ? value.toLocaleString() : '--'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{label}</p>
        </>
      )}
    </div>
  );
};

const SectionError = ({ message }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
    <p className="text-sm text-red-600">{message}</p>
  </div>
);

const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
    ))}
  </div>
);

const formatTimestamp = (ts) => {
  if (!ts) return '--';
  try {
    return format(parseISO(ts), 'MMM d, yyyy h:mm a');
  } catch {
    return ts;
  }
};

const getStatusBadge = (statusCode) => {
  if (statusCode >= 500) {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-yellow-100 text-yellow-800';
};

const getMethodBadge = (statusCode) => {
  if (statusCode >= 500) {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-yellow-100 text-yellow-700';
};

const getRowBorderColor = (statusCode) => {
  if (statusCode >= 500) {
    return 'border-l-4 border-l-red-400';
  }
  return 'border-l-4 border-l-yellow-400';
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const serverErrors = payload.find(p => p.dataKey === 'server_errors');
    const clientErrors = payload.find(p => p.dataKey === 'client_errors');
    const total = (serverErrors?.value || 0) + (clientErrors?.value || 0);
    let formattedLabel = label;
    try {
      formattedLabel = format(parseISO(label), 'MMM d, HH:mm');
    } catch {
      // use raw label
    }
    return (
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm">
        <p className="font-medium text-gray-900 mb-1">{formattedLabel}</p>
        <p className="text-red-600">Server Errors (5xx): {serverErrors?.value || 0}</p>
        <p className="text-yellow-600">Client Errors (4xx): {clientErrors?.value || 0}</p>
        <p className="font-medium text-gray-700 mt-1 pt-1 border-t">Total: {total}</p>
      </div>
    );
  }
  return null;
};

const ErrorsTab = ({ selectedFirmId }) => {
  // Feed state
  const [feedData, setFeedData] = useState(null);
  const [feedPage, setFeedPage] = useState(1);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);

  // Rates state
  const [ratesData, setRatesData] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState(null);

  // Grouped state
  const [groupedData, setGroupedData] = useState(null);
  const [groupedLoading, setGroupedLoading] = useState(true);
  const [groupedError, setGroupedError] = useState(null);

  // Error detail modal state
  const [selectedError, setSelectedError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Fetch core data (rates + grouped) on firmId change
  const fetchCoreData = useCallback(async () => {
    setRatesLoading(true);
    setRatesError(null);
    setGroupedLoading(true);
    setGroupedError(null);

    const results = await Promise.allSettled([
      observabilityService.getErrorRates(selectedFirmId),
      observabilityService.getErrorGroups(selectedFirmId),
    ]);

    if (results[0].status === 'fulfilled') {
      setRatesData(results[0].value);
    } else {
      console.error('Error fetching error rates:', results[0].reason);
      setRatesError('Failed to load error rate data');
    }
    setRatesLoading(false);

    if (results[1].status === 'fulfilled') {
      setGroupedData(results[1].value);
    } else {
      console.error('Error fetching grouped errors:', results[1].reason);
      setGroupedError('Failed to load grouped error data');
    }
    setGroupedLoading(false);
  }, [selectedFirmId]);

  useEffect(() => {
    fetchCoreData();
  }, [fetchCoreData]);

  // Fetch feed (depends on page and firmId)
  useEffect(() => {
    let cancelled = false;
    const fetchFeed = async () => {
      setFeedLoading(true);
      setFeedError(null);
      try {
        const data = await observabilityService.getErrorFeed(selectedFirmId, feedPage, 50);
        if (!cancelled) setFeedData(data);
      } catch (err) {
        console.error('Error fetching error feed:', err);
        if (!cancelled) setFeedError('Failed to load error feed');
      } finally {
        if (!cancelled) setFeedLoading(false);
      }
    };
    fetchFeed();
    return () => { cancelled = true; };
  }, [selectedFirmId, feedPage]);

  // Reset feed page when firm changes
  useEffect(() => {
    setFeedPage(1);
  }, [selectedFirmId]);

  // Error detail handler
  const handleViewDetail = async (errorId) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await observabilityService.getErrorDetail(errorId);
      setSelectedError(data);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setSelectedError({ not_found: true });
      } else {
        console.error('Error fetching error detail:', err);
        setDetailError('Failed to load error details');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedError(null);
    setDetailError(null);
  };

  const feedTotalPages = feedData ? feedData.total_pages || Math.ceil(feedData.total / 50) : 0;

  return (
    <div className="space-y-6">
      {/* Section 1: Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Errors"
          value={ratesData?.summary?.total_errors}
          icon={AlertTriangle}
          color="red"
          loading={ratesLoading}
        />
        <StatCard
          label="Avg / Hour"
          value={ratesData?.summary?.avg_per_hour != null ? Math.round(ratesData.summary.avg_per_hour * 10) / 10 : null}
          icon={Clock}
          color="yellow"
          loading={ratesLoading}
        />
        <StatCard
          label="Spikes Detected"
          value={ratesData?.summary?.spike_count}
          icon={TrendingUp}
          color="orange"
          loading={ratesLoading}
        />
        <StatCard
          label="Error Groups"
          value={groupedData?.groups?.length}
          icon={Layers}
          color="blue"
          loading={groupedLoading}
        />
      </div>

      {/* Section 2: Error Rate Chart */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <BarChart3 className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Error Rate (Hourly)</h3>
        </div>
        {ratesError ? (
          <SectionError message={ratesError} />
        ) : ratesLoading ? (
          <div className="h-72 bg-gray-200 rounded animate-pulse" />
        ) : ratesData?.rates?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ratesData.rates}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                tickFormatter={(h) => {
                  try { return format(parseISO(h), 'MM/dd HH:mm'); } catch { return h; }
                }}
                fontSize={11}
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="server_errors" name="Server Errors (5xx)" stackId="errors">
                {ratesData.rates.map((entry, index) => (
                  <Cell
                    key={`server-${index}`}
                    fill={entry.is_spike ? '#dc2626' : '#ef4444'}
                    fillOpacity={entry.is_spike ? 1 : 0.7}
                  />
                ))}
              </Bar>
              <Bar dataKey="client_errors" name="Client Errors (4xx)" stackId="errors">
                {ratesData.rates.map((entry, index) => (
                  <Cell
                    key={`client-${index}`}
                    fill={entry.is_spike ? '#d97706' : '#f59e0b'}
                    fillOpacity={entry.is_spike ? 1 : 0.7}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-gray-500">No error rate data available</p>
        )}
        {ratesData?.summary?.spike_count > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            Spikes highlighted with darker bars ({ratesData.summary.spike_count} detected)
          </p>
        )}
      </div>

      {/* Section 3: Grouped Errors Table */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Layers className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Top Error Groups</h3>
        </div>
        {groupedError ? (
          <SectionError message={groupedError} />
        ) : groupedLoading ? (
          <LoadingSkeleton rows={6} />
        ) : groupedData?.groups?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occurrences</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Seen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupedData.groups.map((group, idx) => (
                  <tr key={idx} className={`hover:bg-gray-50 ${getRowBorderColor(group.status_code)}`}>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMethodBadge(group.status_code)}`}>
                        {group.method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">{group.path}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(group.status_code)}`}>
                        {group.status_code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-lg font-bold text-gray-900 whitespace-nowrap">{group.occurrence_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatTimestamp(group.first_seen)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatTimestamp(group.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No grouped errors found</p>
        )}
      </div>

      {/* Section 4: Error Feed */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <AlertTriangle className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Recent Errors</h3>
        </div>
        {feedError ? (
          <SectionError message={feedError} />
        ) : feedLoading ? (
          <LoadingSkeleton rows={8} />
        ) : feedData?.items?.length > 0 ? (
          <>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {feedData.items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatTimestamp(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMethodBadge(item.status_code)}`}>
                          {item.method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap max-w-xs truncate" title={item.path}>
                        {item.path}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(item.status_code)}`}>
                          {item.status_code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {item.user_name || item.user_email || '--'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {item.response_time_ms != null ? `${item.response_time_ms}ms` : '--'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {item.status_code >= 500 && item.id && (
                          <button
                            onClick={() => handleViewDetail(item.id)}
                            className="inline-flex items-center px-2 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Details
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {feedTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => setFeedPage((p) => Math.max(1, p - 1))}
                  disabled={feedPage <= 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {feedPage} of {feedTotalPages}
                </span>
                <button
                  onClick={() => setFeedPage((p) => Math.min(feedTotalPages, p + 1))}
                  disabled={feedPage >= feedTotalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">No recent errors found</p>
        )}
      </div>

      {/* Error Detail Modal */}
      {(selectedError || detailLoading || detailError) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeDetailModal} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Error Details</h3>
                <button
                  onClick={closeDetailModal}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {detailLoading ? (
                <LoadingSkeleton rows={6} />
              ) : detailError ? (
                <p className="text-sm text-red-600">{detailError}</p>
              ) : selectedError?.not_found ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No stack trace available for this error.</p>
                  <p className="text-xs text-gray-400 mt-1">Only unhandled server exceptions have captured stack traces.</p>
                </div>
              ) : selectedError ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Error Type</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedError.error_type || '--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Timestamp</p>
                      <p className="text-sm text-gray-900">{formatTimestamp(selectedError.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Path</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedError.method} {selectedError.path}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">User</p>
                      <p className="text-sm text-gray-900">{selectedError.user_name || selectedError.user_email || '--'}</p>
                    </div>
                  </div>

                  {selectedError.error_message && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium mb-1">Error Message</p>
                      <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded">{selectedError.error_message}</p>
                    </div>
                  )}

                  {selectedError.query_string && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium mb-1">Query String</p>
                      <p className="text-sm text-gray-700 font-mono bg-gray-50 px-3 py-2 rounded">{selectedError.query_string}</p>
                    </div>
                  )}

                  {selectedError.stack_trace ? (
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium mb-1">Stack Trace</p>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono max-h-80 overflow-y-auto">
                        {selectedError.stack_trace}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded">
                      <p className="text-sm text-gray-500">No stack trace captured for this error.</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorsTab;
